import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Users, Plus, Trash2, Mail, Phone, X } from "lucide-react";

export default function Contractors() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", skills: "", hourly_rate: "", notes: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contractors");
      setRows(data.contractors || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/contractors", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
        hourly_rate: form.hourly_rate ? parseFloat(form.hourly_rate) : null,
        notes: form.notes,
      });
      toast.success("Contractor added.");
      setCreating(false);
      setForm({ name: "", email: "", phone: "", skills: "", hourly_rate: "", notes: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this contractor?")) return;
    try {
      await api.delete(`/contractors/${id}`);
      setRows((r) => r.filter((x) => x.id !== id));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleStatus = async (row) => {
    try {
      const next = row.status === "active" ? "inactive" : "active";
      const { data } = await api.patch(`/contractors/${row.id}`, { status: next });
      setRows((r) => r.map((x) => (x.id === row.id ? data : x)));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="p-8 max-w-6xl" data-testid="contractors-page">
      <Toaster position="top-right" richColors />
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="overline mb-2 flex items-center gap-2"><Users className="w-3.5 h-3.5" /> Contractor roster</div>
          <h1 className="text-3xl font-bold text-neutral-900">Delivery contractors</h1>
          <p className="text-neutral-600 mt-2 max-w-2xl text-sm">
            Developers, designers, VAs and specialists who execute delivery.
            Assign them to a contract and their back-to-back agreement fires automatically once the client signs.
          </p>
        </div>
        <button onClick={() => setCreating(true)} className="lf-btn-dark text-sm inline-flex items-center gap-2" data-testid="contractors-add-btn">
          <Plus className="w-4 h-4" /> Add contractor
        </button>
      </div>

      {loading ? <div className="text-neutral-500 text-sm">Loading…</div> :
        rows.length === 0 ? (
          <div className="border border-dashed border-neutral-300 bg-white p-16 text-center">
            <Users className="w-8 h-8 mx-auto mb-3 text-neutral-400" />
            <div className="font-display text-xl mb-1">No contractors yet</div>
            <p className="text-neutral-500 text-sm">Add your first delivery contractor to unlock automated agreements.</p>
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {rows.map((c) => (
              <div key={c.id} className="bg-white border border-neutral-200 p-5" data-testid={`contractor-${c.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-display text-lg font-bold">{c.name}</span>
                      <span className={`text-xs font-mono uppercase tracking-widest px-2 py-0.5 border ${c.status === "active" ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-neutral-100 border-neutral-300 text-neutral-600"}`}>
                        {c.status}
                      </span>
                    </div>
                    <div className="text-sm text-neutral-500 mt-1 flex flex-wrap gap-x-4">
                      <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 hover:text-neutral-900"><Mail className="w-3 h-3" /> {c.email}</a>
                      {c.phone && <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 hover:text-neutral-900"><Phone className="w-3 h-3" /> {c.phone}</a>}
                      {c.contractor_number && <span className="inline-flex items-center gap-1 font-mono text-xs bg-neutral-900 text-white px-2 py-0.5">{c.contractor_number}</span>}
                    </div>
                    {c.skills?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {c.skills.map((s) => <span key={s} className="text-xs bg-neutral-100 px-2 py-0.5 font-mono">{s}</span>)}
                      </div>
                    )}
                    {c.hourly_rate && <div className="text-xs text-neutral-500 mt-2 font-mono">${c.hourly_rate}/hr</div>}
                    {c.notes && <div className="text-sm text-neutral-600 mt-2 border-l-2 border-neutral-200 pl-3">{c.notes}</div>}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => toggleStatus(c)} className="text-xs text-neutral-500 hover:text-neutral-900 border border-neutral-300 px-2 py-1">
                      {c.status === "active" ? "Pause" : "Reactivate"}
                    </button>
                    <button onClick={() => remove(c.id)} className="p-1.5 text-neutral-400 hover:text-rose-600" title="Remove"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

      {creating && (
        <div onClick={() => setCreating(false)} className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <form onClick={(e) => e.stopPropagation()} onSubmit={create} className="bg-white w-full max-w-md border border-neutral-900 shadow-2xl">
            <div className="bg-neutral-900 text-white px-6 py-4 flex items-start justify-between">
              <div><div className="text-xs font-mono uppercase tracking-widest text-amber-300">Add contractor</div><div className="text-xl font-bold mt-1">New team member</div></div>
              <button type="button" onClick={() => setCreating(false)} className="text-neutral-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 grid gap-3 text-sm">
              {[
                ["name", "Full name", "text", true],
                ["email", "Email", "email", true],
                ["phone", "Phone", "tel", false],
                ["skills", "Skills (comma-separated)", "text", false],
                ["hourly_rate", "Hourly rate (USD)", "number", false],
              ].map(([k, label, type, req]) => (
                <label key={k} className="grid gap-1">
                  <span className="overline">{label}{req ? " · required" : ""}</span>
                  <input required={req} type={type} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="border border-neutral-300 px-3 py-2" data-testid={`contractor-form-${k}`} />
                </label>
              ))}
              <label className="grid gap-1">
                <span className="overline">Notes</span>
                <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="border border-neutral-300 px-3 py-2 text-sm" />
              </label>
              <button type="submit" className="bg-neutral-900 text-white px-4 py-2.5 font-semibold hover:bg-neutral-700" data-testid="contractor-form-submit">Add contractor</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
